import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCustomersSubtotalsReport } from '../../../../api/hooks/reports/useCustomersSubtotalsReport';
import { PdfDownloadLinkExtendedV2 } from '../../../../components/molecules/pdf/shared/PdfDownloadLinkExtendedV2';
import { addWithDecimals } from '../../../../config/utils/calcUtils';
import { nowDate } from '../../../../config/utils/dateHelpers';
import { Customer } from '../../../../interfaces/customer.interface';
import { SORT_BY_CUSTOMERS, SORT_DIRECTION } from '../../../../interfaces/enumerables';
import { selectSelectedCompany } from '../../../../store/companies/companiesSlice';
import { useAppSelector } from '../../../../store/hooks';
import { CustomersReportPdf } from './pdf/CustomersReportPdf';

interface Props {
  data: Customer[];
}

export const CustomersReportDownloadButton = ({ data }: Props) => {
  const { t } = useTranslation();
  const selectedCompany = useAppSelector(selectSelectedCompany);
  const { isLoading } = useCustomersSubtotalsReport(SORT_BY_CUSTOMERS.COMPANY_NAME, SORT_DIRECTION.ASC);

  const getTotals = useCallback(() => {
    let cust = '0.00';
    let supp = '0.00';
    if (!data || data.length === 0) return { cust, supp };

    for (let i = 0; i < data.length; i++) {
      cust = addWithDecimals(cust, data[i]?.customerSubtotal || '0.00');
      supp = addWithDecimals(supp, data[i]?.supplierSubtotal || '0.00');
    }

    return { cust, supp };
  }, [data]);

  const downloadDoc = useCallback(() => {
    if (selectedCompany) {
      const fileName = `${selectedCompany?.title}_${t('CUSTOMERS_REPORT.DOWNLOAD_REPORT').replaceAll(' ', '')}_${nowDate()}.pdf`;
      const totals = getTotals();

      return (
        <PdfDownloadLinkExtendedV2
          document={
            <CustomersReportPdf
              company={selectedCompany}
              customers={data || []}
              customersTotal={totals.cust}
              suppliersTotal={totals.supp}
            />
          }
          fileName={fileName}
          text={t('FORMS.DOWNLOAD_PDF')}
          isLoading={isLoading}
        />
      );
    }
  }, [selectedCompany, t, getTotals, data, isLoading]);

  return <>{downloadDoc()}</>;
};
